import {
  Box,
  Chip,
  Collapse,
  List,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import {
  ExternalFeatureLayerName,
  MapLayer,
  MapLayerDetail,
  MapLayers,
  MapLayersDetails,
} from 'features/mapping/mapping.constants';
import { useExternalFeatureLayers } from 'features/mapping/hooks/useExternalFeatureLayers';
import { MouseEventHandler, useState } from 'react';
import { useMapLayersState } from 'state/useMapLayersState';
import { SwitchControl } from '../SwitchControl';
import {
  getActiveLayersChipColors,
  layersDrawerChevronButtonSx,
  layersDrawerRowActionsSx,
  layersDrawerRowContentSx,
  layersDrawerRowSx,
  layersDrawerRowTitleSx,
  layersDrawerSwitchSx,
} from './layersDrawer.styles';
import {
  getLayersDrawerMembershipChipLabelKey,
  getLayersDrawerMembershipChipProps,
  layersDrawerChipSx,
} from './layersDrawer.membershipChips';

type CollapsibleExternalLayerGroupProps = {
  mapLayerId: ExternalFeatureLayerName;
  renderControlItem: (
    mapLayer: keyof typeof MapLayersDetails,
    details?: MapLayerDetail,
    policyKey?: MapLayer,
  ) => JSX.Element;
  handleChangeLayer: (
    mapLayer: MapLayer,
    details: MapLayerDetail,
    enabled: boolean,
  ) => void;
};

export const CollapsibleExternalLayerGroup = (
  props: CollapsibleExternalLayerGroupProps,
): JSX.Element | null => {
  const { mapLayerId, renderControlItem, handleChangeLayer } = props;
  const { t } = useTranslation();
  const { externalFeatureLayers } = useExternalFeatureLayers();
  const { mapLayers, disabledLayers } = useMapLayersState();
  const [showChildLayers, setShowChildLayers] = useState(false);

  const featureLayer = externalFeatureLayers.find(
    (layer) => layer.mapLayerId === mapLayerId,
  );

  const layersInGroup = externalFeatureLayers.filter(
    (layer) => layer.parentFeatureLayer === featureLayer?.id,
  );

  const totalLayers = layersInGroup.length;

  const activeLayers = layersInGroup.filter((layer) =>
    mapLayers.includes(layer.mapLayerId),
  ).length;

  const isGroupDisabled =
    disabledLayers.includes(MapLayers.TEAM_LAYER) ||
    layersInGroup.every((layer) => disabledLayers.includes(layer.mapLayerId));

  const isGroupTurnedOn = featureLayer?.treatGroupAsSingleLayer
    ? activeLayers === totalLayers
    : activeLayers > 0;

  const toggleLayers = (): void => {
    const enabled = !isGroupTurnedOn;
    layersInGroup.forEach((layer) => {
      handleChangeLayer(
        layer.mapLayerId,
        {
          title: layer.displayName,
          subtitle: '',
          requiresWebGL2: false,
        },
        enabled,
      );
    });
  };

  const handleClick = (): void => {
    if (featureLayer?.treatGroupAsSingleLayer) {
      toggleLayers();
      return;
    }
    setShowChildLayers((prev) => !prev);
  };

  const handleOnChange: MouseEventHandler<HTMLButtonElement> = (e): void => {
    e.stopPropagation();
    toggleLayers();
  };

  const showExpandChevron = !featureLayer?.treatGroupAsSingleLayer;

  const renderExpandChevron = (): JSX.Element | null => {
    if (!showExpandChevron) {
      return null;
    }
    if (showChildLayers) {
      return <ExpandMoreIcon sx={{ color: 'text.primary' }} />;
    }
    return <ChevronRightIcon sx={{ color: 'text.primary' }} />;
  };

  const titleChip = featureLayer?.treatGroupAsSingleLayer ? (
    <Chip
      {...getLayersDrawerMembershipChipProps(
        t(getLayersDrawerMembershipChipLabelKey('private')),
        'private',
      )}
      sx={layersDrawerChipSx}
      data-testid="private-chip"
    />
  ) : null;

  const activeLayersCountChip = featureLayer?.treatGroupAsSingleLayer ? null : (
    <Chip
      label={t('map.layers.activeItems', {
        count: activeLayers,
        total: totalLayers,
      })}
      variant="watchduty-count"
      component="span"
      data-testid="active-layers-count-chip"
      sx={getActiveLayersChipColors(activeLayers > 0)}
    />
  );

  if (!featureLayer || !layersInGroup.length) {
    return null;
  }

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        aria-label={featureLayer.displayName}
        disabled={isGroupDisabled}
        sx={layersDrawerRowSx}
      >
        <Box sx={layersDrawerRowContentSx}>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            flexWrap="wrap"
            component="span"
          >
            <Typography
              component="span"
              id={featureLayer.displayName}
              color="text.primary"
              sx={layersDrawerRowTitleSx}
            >
              {featureLayer.displayName}
            </Typography>
            {titleChip}
          </Stack>
          {isGroupDisabled && (
            <Typography variant="subtitle1" color="text.secondary">
              {t('map.layers.disabled')}
            </Typography>
          )}
        </Box>
        <Box sx={layersDrawerRowActionsSx}>
          {activeLayersCountChip}
          {showExpandChevron && (
            <Box aria-hidden sx={layersDrawerChevronButtonSx}>
              {renderExpandChevron()}
            </Box>
          )}
          <SwitchControl
            disabled={isGroupDisabled}
            checked={isGroupTurnedOn}
            onChange={handleOnChange}
            listItemEdge={false}
            sx={layersDrawerSwitchSx}
            inputProps={{
              'aria-labelledby': t('map.accessibility.switchLayer'),
              'aria-label': `Toggle ${featureLayer.displayName}`,
            }}
          />
        </Box>
      </ListItemButton>

      {showExpandChevron && (
        <Collapse in={showChildLayers}>
          <List sx={{ pl: 2 }}>
            {layersInGroup.map((layer) =>
              renderControlItem(
                layer.mapLayerId,
                {
                  title: layer.displayName,
                  subtitle: '',
                  requiresWebGL2: false,
                },
                featureLayer.geomType === 'polygon'
                  ? MapLayers.TEAM_POLYGON_LAYER
                  : MapLayers.TEAM_LAYER,
              ),
            )}
          </List>
        </Collapse>
      )}
    </>
  );
};
